import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useActions, useGoals, useManagers, useMe, useOneOnOneStats, useSummary, type Action, type Goal, type Person } from "../api/hooks";
import { Bar } from "../components/charts";
import { SummaryCard } from "../components/summary-card";
import { Chip, Empty, ErrorBox, Loading, Page, Table, type Tone } from "../components/ui";
import { healthTone } from "../lib/health";
import { plural } from "../lib/format";

export const Route = createFileRoute("/management")({ component: ManagementOverview });

/** States an action has reached once someone owns it; completed ÷ these = follow-through. */
const OWNED = new Set(["assigned", "accepted", "in_progress", "completed"]);
const LIVE_GOAL = new Set(["on_track", "at_risk", "off_track", "completed"]);

type ManagerRow = {
  manager: Person;
  oneOnOne: number | null;
  goals: number | null;
  goalsAtRisk: number;
  followThrough: number | null;
  overdueActions: number;
  health: number | null;
  issue: string | null;
};

function pct(num: number, den: number): number | null {
  return den > 0 ? Math.round((100 * num) / den) : null;
}

/** Management health, first cut: the plain mean of the three management rates a manager
 *  has a number for (1:1 completion, goal completion, action follow-through). */
function managementHealth(parts: (number | null)[]): number | null {
  const have = parts.filter((p): p is number => p != null);
  return have.length ? Math.round(have.reduce((a, b) => a + b, 0) / have.length) : null;
}

function rateTone(v: number | null): Tone {
  return v == null ? "neutral" : v >= 80 ? "good" : v >= 50 ? "elevated" : "high";
}

function ManagementOverview() {
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  const managers = useManagers();
  const ones = useOneOnOneStats();
  const goals = useGoals();
  const actions = useActions({ state: "assigned,accepted,in_progress,completed" });
  const summary = useSummary("org", "all");
  // Fixed at first render so "overdue" cannot flip between renders of the same page.
  const [now] = useState(() => Date.now());

  const rows = useMemo<ManagerRow[]>(() => {
    return (managers.data ?? []).map((m) => {
      const s = ones.data?.find((x) => x.manager_person_key === m.person_key);
      const oneOnOne = s ? pct(s.completed, s.completed + s.missed) : null;
      const mine: Goal[] = (goals.data ?? []).filter((g) => g.owner_person_key === m.person_key && LIVE_GOAL.has(g.status));
      const goalsPct = pct(mine.filter((g) => g.status === "on_track" || g.status === "completed").length, mine.length);
      const goalsAtRisk = mine.filter((g) => g.status === "at_risk" || g.status === "off_track").length;
      const owned: Action[] = (actions.data ?? []).filter((a) => a.owner_person_key === m.person_key && OWNED.has(a.state));
      const followThrough = pct(owned.filter((a) => a.state === "completed").length, owned.length);
      const overdueActions = owned.filter((a) => a.state !== "completed" && a.due_at && Date.parse(a.due_at) < now).length;
      const health = managementHealth([oneOnOne, goalsPct, followThrough]);
      const issue =
        oneOnOne != null && oneOnOne < 50 ? "Low 1:1 completion"
        : goalsAtRisk > 0 && goalsPct != null && goalsPct < 50 ? "Most goals at risk"
        : followThrough != null && followThrough < 50 ? "Actions not followed through"
        : overdueActions > 0 ? `${overdueActions} overdue action${overdueActions === 1 ? "" : "s"}`
        : null;
      return { manager: m, oneOnOne, goals: goalsPct, goalsAtRisk, followThrough, overdueActions, health, issue };
    });
  }, [managers.data, ones.data, goals.data, actions.data, now]);

  // Company-wide rates are computed over the records, not averaged over managers,
  // so one manager with two 1:1s does not weigh as much as one with twenty.
  const totals = useMemo(() => {
    const c = (ones.data ?? []).reduce((a, s) => a + s.completed, 0);
    const d = (ones.data ?? []).reduce((a, s) => a + s.completed + s.missed, 0);
    const live = (goals.data ?? []).filter((g) => LIVE_GOAL.has(g.status));
    const good = live.filter((g) => g.status === "on_track" || g.status === "completed").length;
    const risk = live.filter((g) => g.status === "at_risk" || g.status === "off_track").length;
    const owned = (actions.data ?? []).filter((a) => OWNED.has(a.state));
    const done = owned.filter((a) => a.state === "completed").length;
    const overdue = owned.filter((a) => a.state !== "completed" && a.due_at && Date.parse(a.due_at) < now).length;
    const oneOnOne = pct(c, d), goalsPct = pct(good, live.length), followThrough = pct(done, owned.length);
    return { oneOnOne, c, d, goalsPct, live: live.length, risk, riskPct: pct(risk, live.length), followThrough, done, owned: owned.length, overdue, health: managementHealth([oneOnOne, goalsPct, followThrough]) };
  }, [ones.data, goals.data, actions.data, now]);

  const byDept = useMemo(() => {
    const m = new Map<string, ManagerRow[]>();
    for (const r of rows) m.set(r.manager.department ?? "No department", [...(m.get(r.manager.department ?? "No department") ?? []), r]);
    return [...m.entries()]
      .map(([dept, rs]) => {
        const mean = (f: (r: ManagerRow) => number | null) => managementHealth(rs.map(f));
        return { dept, managers: rs.length, health: mean((r) => r.health), oneOnOne: mean((r) => r.oneOnOne), goals: mean((r) => r.goals), followThrough: mean((r) => r.followThrough) };
      })
      .sort((a, b) => (a.health ?? 101) - (b.health ?? 101));
  }, [rows]);

  const attention = useMemo(() => rows.filter((r) => r.issue).sort((a, b) => (a.health ?? 0) - (b.health ?? 0)).slice(0, 6), [rows]);
  const ranked = useMemo(() => byDept.filter((d) => d.health != null), [byDept]);
  const strengths = [...ranked].reverse().slice(0, 3);
  const risks = ranked.slice(0, 3).filter((d) => (d.health ?? 100) < 70);

  if (!isAdmin) return <Page title="Management overview"><Empty title="Admins only" body="Managers see their own line on Team health." /></Page>;
  const pending = managers.isPending || ones.isPending || goals.isPending || actions.isPending;
  const err = managers.error ?? ones.error ?? goals.error ?? actions.error;

  return (
    <Page title="Management overview" sub="Management health, team execution and what needs attention, from 1:1s, goals and actions across every manager.">
      {err && <div className="mb-3"><ErrorBox error={err} /></div>}
      {pending ? <Loading /> : (
        <>
          <div className="grid grid-cols-6 gap-3">
            <Tile label="Management health" value={totals.health} suffix="" tone={healthTone(totals.health)} hint={`Mean of the three rates below. First cut; see docs/signals.md.`} note={plural(rows.length, "manager")} />
            <Tile label="1:1 completion" value={totals.oneOnOne} suffix="%" tone={rateTone(totals.oneOnOne)} note={totals.d ? `${totals.c} of ${totals.d} happened, 90 days` : "none scheduled"} />
            <Tile label="Goal completion" value={totals.goalsPct} suffix="%" tone={rateTone(totals.goalsPct)} note={`${totals.live} live goals on track or done`} />
            <Tile label="Manager follow-through" value={totals.followThrough} suffix="%" tone={rateTone(totals.followThrough)} note={totals.owned ? `${totals.done} of ${totals.owned} owned actions completed` : "no actions owned yet"} />
            <Tile label="Goals at risk" value={totals.riskPct} suffix="%" tone={totals.riskPct == null ? "neutral" : totals.riskPct >= 30 ? "high" : totals.riskPct >= 15 ? "elevated" : "good"} note={`${totals.risk} at risk or off track`} />
            <Tile label="Overdue actions" value={totals.overdue} suffix="" tone={totals.overdue === 0 ? "good" : totals.overdue <= 3 ? "elevated" : "high"} note="owned, past their due date" />
          </div>

          <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <SummaryCard summary={summary.data} pending={summary.isPending} title="What needs attention this week" />
            <div className="card !p-0">
              <div className="px-[18px] pt-4 flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Management health by department</h4><span className="text-[11.5px] text-muted">mean over its managers</span></div>
              <Table
                rows={byDept}
                keyOf={(d) => d.dept}
                empty="No managers found. Reporting lines come from the HRIS connection or a CSV on Organisation."
                cols={[
                  { h: "Department", cell: (d) => <><span className="font-semibold">{d.dept}</span><span className="sub">{d.managers} manager{d.managers === 1 ? "" : "s"}</span></> },
                  { h: "Health", cell: (d) => <div className="flex items-center gap-2"><div className="w-[70px]"><Bar value={d.health} tone={healthTone(d.health)} /></div><span className="font-bold">{d.health ?? "—"}</span></div>, w: "130px" },
                  { h: "1:1s", cell: (d) => <Rate v={d.oneOnOne} />, w: "70px" },
                  { h: "Goals", cell: (d) => <Rate v={d.goals} />, w: "70px" },
                  { h: "Follow-through", cell: (d) => <Rate v={d.followThrough} />, w: "110px" },
                ]}
              />
            </div>
          </div>

          <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: "1.5fr 1fr" }}>
            <div className="card !p-0">
              <div className="px-[18px] pt-4 flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Managers needing attention</h4><Link to="/one-on-ones" className="link">View all managers →</Link></div>
              <Table
                rows={attention}
                keyOf={(r) => r.manager.person_key}
                empty="Nobody is flagged. Every manager is keeping up with their 1:1s, goals and actions."
                cols={[
                  { h: "Manager", cell: (r) => <><span className="font-semibold">{r.manager.full_name ?? r.manager.person_key}</span><span className="sub">{[r.manager.department, r.manager.title].filter(Boolean).join(" · ")}</span></> },
                  { h: "Key issue", cell: (r) => <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--status-high)" }} aria-hidden />{r.issue}</span> },
                  { h: "1:1 completion", cell: (r) => <Rate v={r.oneOnOne} />, w: "120px" },
                  { h: "Goal completion", cell: (r) => <Rate v={r.goals} />, w: "120px" },
                  { h: "Follow-through", cell: (r) => <Rate v={r.followThrough} />, w: "110px" },
                ]}
              />
            </div>
            <div className="card">
              <h4 className="font-bold text-[13.5px]">Top management strengths and risks</h4>
              <div className="grid grid-cols-2 gap-4 mt-3 text-[12.5px]">
                <div>
                  <div className="font-bold mb-2" style={{ color: "var(--status-good)" }}>↑ Biggest strengths</div>
                  {strengths.length === 0 ? <div className="text-muted">Nothing to rank yet.</div> : strengths.map((d, i) => (
                    <div key={d.dept} className="flex gap-2 py-1.5 border-t border-line first:border-t-0"><span className="text-text-4 font-bold">{i + 1}</span><span className="flex-1">Management health in {d.dept}</span><span className="font-bold">{d.health}</span></div>
                  ))}
                </div>
                <div>
                  <div className="font-bold mb-2" style={{ color: "var(--status-high)" }}>↓ Biggest risks</div>
                  {risks.length === 0 ? <div className="text-muted">No department is below 70.</div> : risks.map((d, i) => (
                    <div key={d.dept} className="flex gap-2 py-1.5 border-t border-line first:border-t-0"><span className="text-text-4 font-bold">{i + 1}</span><span className="flex-1">{d.oneOnOne != null && d.oneOnOne < 50 ? "Low 1:1 completion" : d.goals != null && d.goals < 50 ? "Goals slipping" : "Follow-through"} in {d.dept}</span><span className="font-bold">{d.health}</span></div>
                  ))}
                </div>
              </div>
              <div className="text-[11.5px] text-muted mt-4">From live 1:1, goal and action records. Coaching activity and performance risk join this page once those tabs exist.</div>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}

function Tile({ label, value, suffix, tone, note, hint }: { label: string; value: number | null; suffix: string; tone: Tone; note: string; hint?: string }) {
  return (
    <div className="card kpi" title={hint}>
      <h4>{label}</h4>
      <div className="v">
        <span>{value != null ? `${value}${suffix}` : "—"}</span>
        {value != null && tone !== "neutral" && <Chip tone={tone}>{tone === "good" ? "Good" : tone === "elevated" ? "Watch" : "Low"}</Chip>}
      </div>
      <div className="delta">{note}</div>
    </div>
  );
}

function Rate({ v }: { v: number | null }) {
  if (v == null) return <span className="text-muted">—</span>;
  return <span className="font-semibold" style={{ color: v >= 80 ? "var(--status-good)" : v >= 50 ? "var(--status-elevated)" : "var(--status-high)" }}>{v}%</span>;
}
